/**
 * A helper class to the already _"helper class"_ {@link AppSession}. The idea of this class is
 * for it to be extended on each implementation in order to add custom methods to check if the
 * user can access the app specific features.
 */
class AppSessionCapabilities {
  /**
   * @param {AppSession} appSession To get the user list of permissions.
   * @param {AppUtils}   appUtils   To help with the formatting of some parameters.
   */
  constructor(appSession, appUtils) {
    'inject';

    /**
     * A local reference for the `appSession` service.
     * @type {AppSession}
     * @access protected
     */
    this._appSession = appSession;
    /**
     * A local reference for the `appUtils` service.
     * @type {AppUtils}
     * @access protected
     */
    this._appUtils = appUtils;
  }
  /**
   * Get a dictionary with all the permissions the user has. The keys are the name of the
   * permissions and the values are booleans.
   * @return {Object}
   */
  getPermissions() {
    const { account, application } = this._getSession();
    return Object.assign(
      {},
      account ? account.permissions : {},
      application ? application.permissions : {},
    );
  }
  /**
   * Checks whether the user has one or more permissions.
   * @param {string|Array} names       A single permission name or a list of them.
   * @param {boolean}      [any=false] If `true`, the method will return `true` if the user has
   *                                   at least one of the permissions from `names`; but if
   *                                   `false`, it will require from him/her to have them all.
   * @return {boolean}
   */
  hasPermissions(names, any = false) {
    const list = this._appUtils.ensureArray(names);
    const permissions = this.getPermissions();
    return any ?
      list.some((name) => permissions[name] === true) :
      !list.some((name) => permissions[name] !== true);
  }
  /**
   * Validates the permissions from a route. The difference between this method and
   * `hasPermissions` is that `names` is always an Array, and if one of the values is an Array,
   * it would mean that the user can have any of those values.
   * @example
   * // The user needs permissions `one`, `two` and `three`.
   * ['one', 'two', 'three']
   * // The user needs at least one of the _"red permissions"_, as well as `green` and `blue`.
   * [['red', 'dark-red', 'crimson', 'vermillion'], 'green', 'blue']
   *
   * @param {Array}  names A list of permission names.
   * @return {boolean}
   */
  hasRoutePermissions(names) {
    return !names.some((name) => !this.hasPermissions(name, Array.isArray(name)));
  }
  /**
   * Checks whether a user ID matches with the one currently logged in.
   * @param {string}  userId The ID of the user to check.
   * @return {boolean}
   */
  isMe(userId) {
    return this._getSession().user.id === userId;
  }
  /**
   * Gets all the session information for the rest of the methods to use.
   * @return {Object}
   * @access protected
   */
  _getSession() {
    return this._appSession.getSession();
  }
}

export { AppSessionCapabilities };
